import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { contentWrite, createPreviewPR } from "../../services/content";
import PublishBar from "../../components/admin/PublishBar";

const AboutAdmin: React.FC = () => {
  const [tr, setTr] = React.useState<any>({});
  const [en, setEn] = React.useState<any>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [publishMode, setPublishMode] = React.useState<"prod" | "preview">(
    "prod"
  );
  const [commitMsg, setCommitMsg] = React.useState("chore(about): update");
  const [branchName, setBranchName] = React.useState("");
  const [prUrl, setPrUrl] = React.useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [rtr, ren] = await Promise.all([
        fetch("/data/pages/about/tr.json", { cache: "no-store" }).then((r) =>
          r.json()
        ),
        fetch("/data/pages/about/en.json", { cache: "no-store" })
          .then((r) => r.json())
          .catch(() => ({})),
      ]);
      setTr(rtr || {});
      setEn(ren || {});
    } catch (e: any) {
      setError(e?.message || "Veri okunamadı");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    setError("");
    try {
      const branch =
        publishMode === "preview"
          ? branchName || `preview/about-${Date.now()}`
          : undefined;
      await Promise.all([
        contentWrite(
          "data/pages/about/tr.json",
          JSON.stringify(tr, null, 2),
          `${commitMsg} tr`,
          branch
        ),
        contentWrite(
          "data/pages/about/en.json",
          JSON.stringify(en, null, 2),
          `${commitMsg} en`,
          branch
        ),
      ]);
      if (publishMode === "preview" && branch) {
        const pr = await createPreviewPR(branch, "About Page Update");
        setPrUrl(pr.url);
      }
      setSuccess("Kaydedildi");
    } catch (e: any) {
      setError(e?.message || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  const bind = (lang: "tr" | "en", path: string, value: any) => {
    const set = lang === "tr" ? setTr : setEn;
    set((d: any) => {
      const copy = { ...d };
      const keys = path.split(".");
      let cur: any = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        cur[k] = cur[k] || {};
        cur = cur[k];
      }
      cur[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          Hakkımızda İçeriği
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          İpucu: TR ve EN içerikleri yan yana düzenleyin. Kaydet dediğinizde iki
          dosya birlikte güncellenir; Preview PR ile gözden geçirme akışını
          kullanabilirsiniz.
        </Alert>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <PublishBar
            commitMsg={commitMsg}
            setCommitMsg={setCommitMsg}
            publishMode={publishMode}
            setPublishMode={setPublishMode}
            branchName={branchName}
            setBranchName={setBranchName}
            branchPlaceholder={`preview/about`}
          />
          <Button
            variant="contained"
            onClick={save}
            disabled={loading}
          >
            Değişiklikleri Yayına Al
          </Button>
        </Stack>

        <Grid container spacing={3}>
          {[
            { label: "Türkçe", lang: "tr", data: tr },
            { label: "English", lang: "en", data: en },
          ].map((cfg) => (
            <Grid item xs={12} md={6} key={cfg.lang}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    {cfg.label}
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      label="Hero Başlık 1"
                      value={cfg.data?.hero?.titleLine1 || ""}
                      onChange={(e) =>
                        bind(cfg.lang as any, "hero.titleLine1", e.target.value)
                      }
                      fullWidth
                    />
                    <TextField
                      label="Hero Başlık 2"
                      value={cfg.data?.hero?.titleLine2 || ""}
                      onChange={(e) =>
                        bind(cfg.lang as any, "hero.titleLine2", e.target.value)
                      }
                      fullWidth
                    />
                    <TextField
                      label="Hero Alt Başlık"
                      value={cfg.data?.hero?.subtitle || ""}
                      onChange={(e) =>
                        bind(cfg.lang as any, "hero.subtitle", e.target.value)
                      }
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Hikaye Paragraf 1"
                      value={cfg.data?.story?.p1 || ""}
                      onChange={(e) =>
                        bind(cfg.lang as any, "story.p1", e.target.value)
                      }
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Hikaye Paragraf 2"
                      value={cfg.data?.story?.p2 || ""}
                      onChange={(e) =>
                        bind(cfg.lang as any, "story.p2", e.target.value)
                      }
                      fullWidth
                      multiline
                      minRows={2}
                    />
                    <TextField
                      label="Misyon"
                      value={cfg.data?.missionText || ""}
                      onChange={(e) =>
                        bind(cfg.lang as any, "missionText", e.target.value)
                      }
                      fullWidth
                      multiline
                      minRows={3}
                    />
                    <TextField
                      label="Vizyon"
                      value={cfg.data?.visionText || ""}
                      onChange={(e) =>
                        bind(cfg.lang as any, "visionText", e.target.value)
                      }
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={load} disabled={loading}>
            Yenile
          </Button>
        </Stack>

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          autoHideDuration={5000}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          onClose={() => setSuccess("")}
          autoHideDuration={2500}
        >
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
            {prUrl && (
              <Button
                component="a"
                href={prUrl}
                target="_blank"
                size="small"
                sx={{ ml: 1 }}
              >
                PR'ı Aç
              </Button>
            )}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AboutAdmin;
